import React, { type ReactNode } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';
import { spacing } from '../constants/spacing';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { gridItemWidthPercent } from '../utils/responsiveLayout';
import { isWebPlatform } from '../utils/webLayout';

const WEB_GRID_GAP = spacing.lg;

/** Web: equal gutters so a short last row stays left-aligned instead of spreading. */
function webItemWidth(columns: number): ViewStyle['width'] {
  if (columns <= 1) return '100%';
  const gaps = WEB_GRID_GAP * (columns - 1);
  return `calc((100% - ${gaps}px) / ${columns})` as ViewStyle['width'];
}

type Props = {
  children: ReactNode;
  style?: ViewStyle;
  /** Override automatic column count (2–4 from viewport). */
  columns?: number;
};

function flattenChildren(children: ReactNode): React.ReactNode[] {
  const items: React.ReactNode[] = [];
  React.Children.forEach(children, child => {
    if (child == null || child === false) return;
    if (React.isValidElement(child) && child.type === React.Fragment) {
      items.push(...flattenChildren((child.props as { children?: ReactNode }).children));
      return;
    }
    items.push(child);
  });
  return items;
}

export function ResponsiveGrid({ children, style, columns }: Props) {
  const layout = useResponsiveLayout();
  const count = columns ?? layout.gridColumns;
  /** Web tablet+ only; phones keep the native percentage layout. */
  const web = isWebPlatform() && !layout.isCompact;
  const itemWidth = web ? webItemWidth(count) : gridItemWidthPercent(count);

  return (
    <View style={[styles.grid, web ? styles.gridWeb : null, style]}>
      {flattenChildren(children).map((child, index) => (
        <View key={index} style={{ width: itemWidth, minWidth: 0, maxWidth: '100%' }}>
          {child}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: spacing.md,
    width: '100%',
  },
  gridWeb: {
    justifyContent: 'flex-start',
    columnGap: WEB_GRID_GAP,
  },
});
